import { consume } from '@lit/context';
import { localized, t } from '@weavedev/lit-i18next';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { iconCheckmarkCircle, iconExcel, iconPdf } from '@/components/icons';
import { global, GlobalComponentType } from '@/context';
import {
  BREAKPOINT_EXTRA_LARGE_PX,
  BREAKPOINT_LARGE_PX,
  BREAKPOINT_MEDIUM_PX,
  BREAKPOINT_SMALL_PX,
} from '@/settings';
import { baseStyles, buttonStyles } from '@/styles';
import { DownloadItem, DownloadStatus } from '@/types';

import '../loader/fg-loader';

@customElement('fg-downloads')
@localized()
export class FgDownloads extends LitElement {
  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  @property({ type: String })
  downloadStatus!: { [key: string]: DownloadStatus };

  @property({ type: Array })
  downloads: DownloadItem[] = [];

  downloadFile(download: DownloadItem) {
    this.dispatchEvent(
      new CustomEvent('download-file', { detail: { download } }),
    );
  }

  renderButton(download: DownloadItem) {
    return this.downloadStatus &&
      (this.downloadStatus[download.id] === DownloadStatus.INITIAL ||
        this.downloadStatus[download.id] === DownloadStatus.ERROR)
      ? html`
          <button
            @click=${() => this.downloadFile(download)}
            class="button button-primary"
          >
            ${t('labels.downloadButton')}
          </button>
        `
      : this.downloadStatus &&
          this.downloadStatus[download.id] === DownloadStatus.DOWNLOADING
        ? html`
            <div class="loading-button-container">
              <button class="button button-primary button-loading">
                ${t('labels.loading')}...
              </button>
              <div class="loading-spinner-container">
                <fg-loader type="small" .bgcolor=${'transparent'}></fg-loader>
              </div>
            </div>
          `
        : html`
            <button class="button button-success">
              <div class="success-icon">${iconCheckmarkCircle}</div>
            </button>
          `;
  }

  renderFileExtensionLabel(type: DownloadItem['type']) {
    switch (type) {
      case 'excel':
        return html`
          <span>
            <span class="excel-icon">${iconExcel}</span>
            ${t('labels.excelSheet')}
          </span>
        `;
      case 'pdf':
        return html`
          <span>
            <span class="pdf-icon">${iconPdf}</span>
            ${t('labels.pdf')}
          </span>
        `;
      default:
        return nothing;
    }
  }

  render() {
    return html`
      <div class="list-container">
        ${this.downloads?.map(
          download => html`
            <div class="list">
              <div class="title">${t(download.name)}</div>
              <div class="content">
                <div class="type-container">
                  ${this.renderFileExtensionLabel(download.type)}
                  ${this.downloadStatus &&
                  this.downloadStatus[download.id] === DownloadStatus.ERROR
                    ? html`
                        <span class="error-message">
                          ${t('errors.somethingWentWrong')}
                        </span>
                      `
                    : nothing}
                </div>
                ${this.renderButton(download)}
              </div>
            </div>
          `,
        )}
      </div>
    `;
  }

  static styles = [
    baseStyles,
    buttonStyles,
    css`
      .list-container {
        display: grid;
        gap: 1.5rem;
      }

      .list {
        background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));
        border-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
        display: flex;
        flex-direction: column;
        height: 100%;
        justify-content: space-between;
        padding: 1rem;
      }

      .title {
        color: var(--fg-gray--900, var(--fg-fallback-gray--900));
        font-size: 1.125rem;
        font-weight: 600;
        margin-bottom: 0.25rem;
      }

      .content {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
      }

      .type-container {
        align-items: baseline;
        display: flex;
        flex-direction: column;
        margin-bottom: 1rem;
        flex-grow: 1;
      }

      .type-container > span {
        align-items: center;
        display: flex;
        gap: 0.25rem;
      }

      .pdf-icon {
        display: flex;
        width: 17px;
        height: 23px;
      }

      .excel-icon {
        color: var(--fg-success--dark, var(--fg-fallback-success--dark));
        display: flex;
      }

      .list .button {
        width: 100%;
      }

      .loading-button-container {
        position: relative;
      }

      .button-loading {
        opacity: 40%;
      }

      .loading-spinner-container {
        left: 0;
        position: absolute;
        right: 0;
        top: 0;
      }

      .button-success {
        background-color: var(--fg-success, var(--fg-fallback-success));
      }

      .success-icon {
        color: #fff;
        display: flex;
        justify-content: center;
      }

      .error-message {
        color: var(--fg-error--dark, var(--fg-fallback-error--dark));
        display: block;
        width: 100%;
      }

      @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
        .list-container {
          width: 100%;
        }
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        .list-container {
          width: 100%;
        }
      }

      @media screen and (min-width: ${BREAKPOINT_LARGE_PX}px) {
        .list-container {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
      }

      @media screen and (min-width: ${BREAKPOINT_EXTRA_LARGE_PX}px) {
        .list-container {
          grid-template-columns: repeat(3, minmax(300px, 1fr));
        }
      }
    `,
  ];
}
